<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { T } from '@tolgee/vue'
import { CARD_COLORS } from '@/enums/common'
import StrokeCoinImage from '@/assets/images/icons/gold-coin-icon-s3.webp'
import Collect1xImage from '@/assets/images/prize-cards/collect1x.png'
import Collect3xImage from '@/assets/images/prize-cards/collect3x.png'
import { formatNumberFullTruncate } from '@/helpers/utils'
import BaseButton from '@/components/base/BaseButton.vue'
import PrizeCard from '@/components/PrizeCard.vue'
import ShopItemDetails from '@/components/ShopItemDetails.vue'
import { ref, watch, computed } from 'vue'
import { useEcommercePrice } from '@/composables/game/ecommerce'
import { type EcommerceItem } from '@/types/shared/player-config'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { GameSound, useSounds } from '@/composables/game/sounds'

const props = defineProps<{
  show: boolean
  points?: number
  spins?: number
  ecommerceItem: EcommerceItem
}>()

defineEmits<{
  (e: 'hide'): void
  (e: 'onCollect1x'): void
  (e: 'purchase', result: { paid: boolean, nonce?: string }): void
}>()

const { getStarPrice } = useEcommercePrice()
const { addHapticFeedback, addNotificationFeedback } = useHapticFeedback()
const { playSound } = useSounds()

const showConfirmation = ref(false)
const usdAmount = computed(() => getStarPrice(props.ecommerceItem)?.usdAmount ?? 0)
watch(() => props.show, (show) => {
  if (show) {
    showConfirmation.value = false
  }
})

</script>
<template>
  <TheBottomSheet
    :show="show"
    :back-button="showConfirmation"
    content-class="flex flex-col px-0 pt-0"
    @hide="$emit('hide')"
    @back="showConfirmation = false"
  >
    <!-- Confirmation screen with ShopItemDetails -->
    <div
      v-if="showConfirmation"
      class="pt-12 pb-8 h-full flex flex-col items-center text-center overflow-auto px-6 max-h-[600px]"
    >
      <ShopItemDetails
        :image="Collect3xImage"
        :item="ecommerceItem"
        :coins-to-earn="points ? points * 3 : undefined"
        :spins-to-earn="spins ? spins * 3 : undefined"
        @hide="(result) => $emit('purchase', result)"
      />
    </div>

    <!-- Card selection screen -->
    <div
      v-else
      class="pt-12 pb-8 h-full flex flex-col items-center justify-between text-center overflow-auto px-6 max-h-[600px] gap-y-8"
    >
      <div class="h-full w-full flex flex-col items-center">
        <img
          src="@/assets/images/gifs/calendar.gif"
          class="size-[120px] mb-6"
        >
        <h2 class="text-light-white text-2xl font-bold -tracking-[0.48px]">
          <T
            key-name="tasks.daily_reward.modal.title"
            default-value="Check-in Reward"
          />
        </h2>
        <p class="font-medium text-base text-white/60">
          <T
            key-name="tasks.daily_reward.modal.description"
            default-value="Choose one option from the rewards below"
          />
        </p>
      </div>
      <div
        class="grid grid-cols-2 justify-between gap-3 w-full"
      >
        <PrizeCard
          :color="CARD_COLORS.GRAY"
          card-class="border-[#545351]"
          @click="addHapticFeedback('medium'); addNotificationFeedback('success'); playSound(GameSound.PURCHASE_SUCCESS); $emit('onCollect1x')"
        >
          <div class="flex flex-col items-center flex-1 justify-center">
            <div class="flex flex-col gap-y-2 items-center">
              <div
                v-if="points"
                class="flex gap-1 items-center"
              >
                <img
                  :src="StrokeCoinImage"
                  class="h-6 w-auto object-contain"
                >
                <span
                  class="text-[24px] leading-[18px] font-[800] text-white black-stroke disable-slashed-zero"
                >
                  {{
                    formatNumberFullTruncate(points)
                  }}
                </span>
              </div>
              <p
                v-if="spins"
                class="text-white black-stroke text-[24px] leading-[18px] font-[800] disable-slashed-zero"
              >
                <T
                  key-name="fortune_wheel.info.more_spins.count_spin"
                  default-value="{count, plural, one {# spin} other {# spins}}"
                  :params="{count: spins}"
                />
              </p>
            </div>

            <div class="w-[100px] mt-[22px]">
              <img
                :src="Collect1xImage"
                class="w-full h-auto"
              >
            </div>
          </div>
          <BaseButton
            button-class="px-1 h-[48px]"
            class="w-full"
          >
            <T
              key-name="tasks.daily_reward.modal.claim"
              default-value="Claim"
            />
          </BaseButton>
        </PrizeCard>
        <PrizeCard
          :color="CARD_COLORS.BLUE"
          @click="showConfirmation = true"
        >
          <div class="flex flex-col items-center flex-1 justify-center">
            <div class="flex flex-col gap-y-2 items-center">
              <div
                v-if="points"
                class="flex gap-1 items-center"
              >
                <img
                  :src="StrokeCoinImage"
                  class="h-6 w-auto object-contain"
                >
                <span
                  class="text-[24px] leading-[18px] font-[800] text-white black-stroke disable-slashed-zero"
                >
                  {{
                    formatNumberFullTruncate((points ?? 0) * 3)
                  }}
                </span>
              </div>
              <p
                v-if="spins"
                class="text-white black-stroke text-[24px] leading-[18px] font-[800] disable-slashed-zero"
              >
                <T
                  key-name="fortune_wheel.info.more_spins.count_spin"
                  default-value="{count, plural, one {# spin} other {# spins}}"
                  :params="{count: (spins ?? 0) * 3}"
                />
              </p>
            </div>
            <div class="w-[100px]  mt-[22px]">
              <img
                :src="Collect3xImage"
                class="w-full h-auto"
              >
            </div>
          </div>
          <div
            class="w-full flex  rounded-[13px] p-0.5 green-gradient"
          >
            <BaseButton
              style-type="primary-pink"
              button-class="px-0 h-[48px] gap-x-1.5"
              class="w-full"
            >
              ${{ formatNumberFullTruncate(usdAmount, true) }}
            </BaseButton>
          </div>
        </PrizeCard>
      </div>
    </div>
  </TheBottomSheet>
</template>
<style>

.green-gradient {
  background: linear-gradient(91.99deg, rgba(254, 78, 254, 0.75) -4.79%, rgba(20, 241, 255, 0.75) 23.5%);
  box-shadow: 0px 0px 10.13px 0px rgba(215, 115, 215, 1);
}
</style>